import { useCallback, useEffect, useRef, useState } from 'react';
import { Building2, ChevronRight, Loader2, Search, User } from 'lucide-react';
import manSearchBig from 'assets/images/icons/dashboard/man-search-big.svg';
import newMain from 'assets/images/icons/dashboard/new-main.svg';
import {
  EligibleSubscriberDto,
  searchEligibleSubscribers,
} from '../api/groupContractSubscribersApi';

interface EligibleSubscriberSearchProps {
  /** Contrat de groupe dont on cherche les souscripteurs éligibles. */
  contractId: string;
  /** Appelé avec le personId quand l'utilisateur sélectionne une personne de la liste. */
  onSelectPerson: (personId: string) => void;
  /**
   * Callback de création d'une nouvelle personne. Fourni uniquement pour les contrats
   * de portée PersonneMorale — absent → la carte "créer" n'est pas affichée.
   */
  showAddForm?: () => void;
  /** True pendant l'envoi d'une sélection : la liste est désactivée (feedback + anti-double-clic). */
  isSubmitting?: boolean;
  title?: string;
  searchPlaceholder?: string;
  createLabel?: string;
}

const PAGE_SIZE = 50;

const SourceBadge = ({ source }: { source: EligibleSubscriberDto['source'] }) => {
  const isCabinet = source === 'CABINET';
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-xs font-medium ${
        isCabinet ? 'bg-amber-100 text-amber-800' : 'bg-indigo-100 text-indigo-800'
      }`}
    >
      {isCabinet ? 'Cabinet' : 'Portefeuille partagé'}
    </span>
  );
};

export const EligibleSubscriberSearch = ({
  contractId,
  onSelectPerson,
  showAddForm,
  isSubmitting = false,
  title,
  searchPlaceholder,
  createLabel,
}: EligibleSubscriberSearchProps) => {
  const [search, setSearch] = useState('');
  const [items, setItems] = useState<EligibleSubscriberDto[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const runSearch = useCallback(
    async (term: string) => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await searchEligibleSubscribers(contractId, term, 1, PAGE_SIZE);
        setItems(response.items ?? []);
        setTotalCount(response.totalCount ?? 0);
      } catch {
        setError('Impossible de charger les souscripteurs éligibles.');
        setItems([]);
        setTotalCount(0);
      } finally {
        setIsLoading(false);
      }
    },
    [contractId],
  );

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(search.trim()), 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [search, runSearch]);

  return (
    <div className="max-w-5xl mx-auto">
      <div className="flex gap-8 w-full flex-col rounded-xl border bg-white px-8 py-14 text-center shadow">
        <div className="flex flex-col gap-2">
          <div className="flex justify-center items-center">
            <img src={manSearchBig} alt="search" />
          </div>
          <h2 className="text-lg font-bold text-gray-900">
            {title ?? 'Quel souscripteur pour ce contrat collectif ?'}
          </h2>
        </div>

        <div className="mx-auto w-full max-w-2xl">
          <div className="relative">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              size={18}
            />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={searchPlaceholder ?? 'Rechercher par nom ou SIRET...'}
              className="w-full rounded-lg border border-gray-300 py-2.5 pl-10 pr-4 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          <div className="mt-4 max-h-80 overflow-y-auto rounded-lg border border-gray-200 divide-y divide-gray-100">
            {isLoading && (
              <div className="flex items-center justify-center gap-2 py-8 text-sm text-gray-500">
                <Loader2 className="animate-spin" size={16} />
                Chargement...
              </div>
            )}

            {!isLoading && error && (
              <div className="py-8 text-sm text-red-600">{error}</div>
            )}

            {!isLoading && !error && items.length === 0 && (
              <div className="py-8 text-sm text-gray-500">
                Aucun souscripteur éligible
                {search.trim() ? ' pour cette recherche.' : '.'}
              </div>
            )}

            {!isLoading
              && !error
              && items.map((item) => {
                const isPm = item.type === 'LEGAL';
                return (
                  <button
                    key={item.personId}
                    type="button"
                    onClick={() => onSelectPerson(item.personId)}
                    disabled={isSubmitting}
                    className="group flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <div
                      className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg ${
                        isPm ? 'bg-purple-50 text-purple-600' : 'bg-sky-50 text-sky-600'
                      }`}
                    >
                      {isPm ? <Building2 size={16} /> : <User size={16} />}
                    </div>
                    <div className="flex min-w-0 flex-1 flex-col">
                      <span className="truncate text-sm font-medium text-gray-900">
                        {item.name}
                      </span>
                      <span className="text-xs text-gray-500">
                        {isPm ? `SIRET : ${item.siret || '—'}` : 'Personne physique'}
                      </span>
                    </div>
                    <SourceBadge source={item.source} />
                    <ChevronRight
                      size={16}
                      className="text-gray-300 transition-transform group-hover:translate-x-0.5 group-hover:text-gray-500"
                    />
                  </button>
                );
              })}
          </div>

          {!isLoading && !error && totalCount > items.length && (
            <p className="mt-2 text-xs text-gray-400">
              {totalCount - items.length} résultat(s) supplémentaire(s) — affinez votre recherche.
            </p>
          )}
        </div>

        {showAddForm && (
          <button
            type="button"
            onClick={showAddForm}
            className="p-8 max-w-2xl w-full mx-auto flex flex-col items-center gap-4 bg-white border shadow rounded-2xl"
          >
            <div className="flex justify-center items-center">
              <img src={newMain} alt="new" />
            </div>
            <p className="text-dark font-w700">
              {createLabel ?? 'Créer un nouveau souscripteur'}
            </p>
          </button>
        )}
      </div>
    </div>
  );
};
